import React, { useEffect, useState } from "react";
import { getDatabase, onValue, push, ref, remove } from "firebase/database";
import { Trash2, Plus } from "lucide-react";

import { useNotification } from "../../../contexts/NotificationContext";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Item } from "./Item";
import { Title } from "./Title";

interface SuggestionEntry {
  key: string;
  value: string;
}

export default function Suggestions() {
  const [suggestions, setSuggestions] = useState<SuggestionEntry[]>([]);
  const [newWord, setNewWord] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const { showNotification } = useNotification();

  useEffect(() => {
    const db = getDatabase();
    const suggestionsRef = ref(db, "suggestions");

    const unsubscribe = onValue(
      suggestionsRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setSuggestions(
            Object.entries<string>(data).map(([key, value]) => ({ key, value }))
          );
        } else {
          setSuggestions([]);
        }
      },
      () => showNotification("error", "Erro ao carregar sugestões.")
    );

    return () => unsubscribe();
  }, [showNotification]);

  const handleAddSuggestion = async () => {
    if (!newWord.trim()) {
      return showNotification("warning", "Adicione um texto antes de salvar.");
    }

    try {
      const db = getDatabase();
      await push(ref(db, "suggestions"), newWord.trim());
      showNotification("success", `Sugestão '${newWord}' criada com sucesso.`);
      setNewWord("");
      setIsEditing(false);
    } catch (error) {
      showNotification("error", "Erro ao adicionar sugestão.");
    }
  };

  const handleDeleteSuggestion = async (suggestion: SuggestionEntry) => {
    try {
      const db = getDatabase();
      await remove(ref(db, `suggestions/${suggestion.key}`));
      showNotification(
        "success",
        `Sugestão '${suggestion.value}' removida com sucesso.`
      );
    } catch (error) {
      showNotification("error", "Erro ao remover sugestão.");
    }
  };

  return (
    <section>
      <Title className="mb-4">Sugestões</Title>
      <Item className="space-y-4">
        {!isEditing && suggestions.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Nenhuma sugestão adicionada ainda.
          </p>
        )}
        {!isEditing && suggestions.length > 0 && (
          <div className="space-y-2">
            {suggestions.map((suggestion) => (
              <div
                key={suggestion.key}
                className="flex items-center justify-between rounded-2xl border border-border/80 px-3 py-2"
              >
                <span className="text-sm font-medium text-white">
                  {suggestion.value}
                </span>
                <button
                  type="button"
                  onClick={() => handleDeleteSuggestion(suggestion)}
                  className="rounded-full p-1 text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {isEditing && (
          <div className="space-y-3">
            <Input
              required
              placeholder="Ex. Aeroporto Hercílio Luz"
              value={newWord}
              onChange={(e) => setNewWord(e.target.value)}
            />
            <Button className="w-full" onClick={handleAddSuggestion}>
              Salvar sugestão
            </Button>
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => setIsEditing(false)}
            >
              Cancelar
            </Button>
          </div>
        )}

        {!isEditing && (
          <Button
            variant="ghost"
            className="w-full border border-dashed border-border"
            onClick={() => setIsEditing(true)}
          >
            <Plus className="mr-2 h-4 w-4" /> Adicionar sugestão
          </Button>
        )}
      </Item>
    </section>
  );
}
